import {
  Bell,
  ChevronRight,
  Home,
  LogOut,
  Loader2,
  MoreVertical,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { ThemeToggle } from "@/design-system/providers/ThemeToggle";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useNotificationsUnreadCount } from "@/hooks/useNotificationsInbox";
import { useAuthStore } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { SiteIcon } from "@/components/icons/siteIcons";
import {
  consoleCareNavigation,
  consoleDatabaseNavigation,
  consoleDashboardNavigation,
  consoleHomeNavigation,
  consoleManagementNavigation,
} from "@/constants/consoleNavigation";
import { meetsPermissionRequirement } from "@/constants/permissions";
import { iconography } from "@/design-system/tokens/iconography";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarSeparator,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { ZarityMark } from "@/components/icons/ZarityMark";
import versionYml from "../../version.yml?raw";
import { isStaging } from "@/lib/env";
import { useConfirmUnsaved } from "@/hooks/useUnsavedChanges";

const parsedVersion =
  versionYml
    .split("\n")
    .find((line) => line.startsWith("version:"))
    ?.split(":")[1]
    ?.trim() || "0.0.0";

/**
 * The bell, shared by the expanded and collapsed sidebar states.
 *
 * Module scope on purpose: defined inside AppSidebar it would be a new
 * component type on every render, remounting and flickering the badge.
 *
 * The count is conversations, not messages — four patients waiting reads "4",
 * which is what the nudges contract specifies. It reads the shared
 * `/nudges/console/unread-count/` query that `usePingNotifications` polls, so
 * the bell, favicon and alert always agree.
 */
const NotificationBell = ({ onOpen }: { onOpen: () => void }) => {
  const location = useLocation();
  const { data } = useNotificationsUnreadCount();

  const active = location.pathname.startsWith("/notifications");
  const unread = data?.unread_count ?? 0;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        "relative h-8 w-8 shrink-0 rounded-full border border-sidebar-border/60 bg-sidebar-accent/40 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        active &&
          "bg-sidebar-primary/12 text-sidebar-primary hover:bg-sidebar-primary/16"
      )}
      onClick={onOpen}
      aria-label={
        unread > 0
          ? `Open notifications, ${unread} unread`
          : "Open notifications"
      }
    >
      <Bell className="size-4" />
      {unread > 0 ? (
        <span
          aria-hidden="true"
          className="z-caption absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 font-medium tabular-nums text-primary-foreground"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      ) : null}
    </Button>
  );
};

export const AppSidebar = () => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { getUserRole, hasAnyPermission, hasPermission, logout, user } =
    useAuthStore();
  const { state, toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const confirmUnsaved = useConfirmUnsaved();
  const userName =
    user && `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
      : "User";
  const sidebarUserName =
    user?.first_name && userName.length > 13 ? user.first_name : userName;
  const userInitials =
    `${user?.first_name?.[0] || "U"}${user?.last_name?.[0] || "N"}`.toUpperCase();
  const SidebarToggleIcon =
    state === "collapsed" ? PanelLeftOpen : PanelLeftClose;
  const currentUserRole = (
    getUserRole() ||
    user?.user_type ||
    "user"
  ).toLowerCase();
  const userRoleLabel = currentUserRole
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  const canAccessNavigationItem = (item: {
    permissionRequirement: Parameters<typeof meetsPermissionRequirement>[0];
  }) =>
    meetsPermissionRequirement(item.permissionRequirement, {
      hasPermission,
      hasAnyPermission,
    });

  const guardedNavigate = (path: string) => {
    if (location.pathname === path) return;
    confirmUnsaved(() => navigate(path));
  };

  const doLogout = async () => {
    if (isLoggingOut) return;

    try {
      setIsLoggingOut(true);
      logout();
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleLogout = () => {
    confirmUnsaved(() => {
      void doLogout();
    });
  };

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return (
        (location.pathname === "/dashboard" ||
          location.pathname.startsWith("/dashboard/")) &&
        !location.pathname.includes("/tracker")
      );
    }

    if (path.startsWith("/settings/appointments")) {
      return location.pathname.includes("/settings/appointments");
    }

    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const isAdminItemActive = (
    itemId:
      | (typeof consoleCareNavigation)[number]["id"]
      | (typeof consoleManagementNavigation)[number]["id"]
      | (typeof consoleDatabaseNavigation)[number]["id"]
  ) => {
    switch (itemId) {
      case "treatment-overview":
        return isActive("/treatment-overview");
      case "patients":
        return isActive("/patients");
      case "tracker":
        return isActive("/tracker");
      case "medicine":
        return location.pathname.includes("/settings/medicine");
      case "nutrition":
        return location.pathname.includes("/settings/nutrition");
      case "diseases":
        return location.pathname.includes("/settings/diseases");
      case "staff":
        return location.pathname.includes("/settings/staff");
      case "communications":
        return location.pathname.includes("/settings/communications");
      case "consultation-recordings":
        return location.pathname.includes("/settings/consultation-recordings");
      case "transactions":
        return location.pathname.includes("/settings/transactions");
      case "maintenance-windows":
        return location.pathname.includes("/settings/maintenance-windows");
      case "services":
        return location.pathname.includes("/settings/services");
      case "promo-codes":
        return location.pathname.includes("/settings/promo-codes");
    }
  };

  const dashboards = consoleDashboardNavigation
    .filter(canAccessNavigationItem)
    .map((item) => ({
      title: item.label,
      iconKey: item.iconKey,
      url: item.href,
      badge: null,
      isActive: isActive(item.href),
    }));

  const careItems = consoleCareNavigation
    .filter(canAccessNavigationItem)
    .map((item) => ({
      ...item,
      isActive: isAdminItemActive(item.id),
    }));

  const managementItems = consoleManagementNavigation
    .filter(canAccessNavigationItem)
    .map((item) => ({
      ...item,
      isActive: isAdminItemActive(item.id),
    }));

  const databaseItems = consoleDatabaseNavigation
    .filter(canAccessNavigationItem)
    .map((item) => ({
      ...item,
      isActive: isAdminItemActive(item.id),
    }));
  const canAccessHome = canAccessNavigationItem(consoleHomeNavigation);

  const sections = [
    ...(dashboards.length > 0 || canAccessHome
      ? [{ label: "Dashboards", items: dashboards }]
      : []),
    ...(careItems.length > 0 ? [{ label: "Patients", items: careItems }] : []),
    ...(managementItems.length > 0
      ? [{ label: "Operations", items: managementItems }]
      : []),
    ...(databaseItems.length > 0
      ? [{ label: "Library", items: databaseItems }]
      : []),
  ];

  return (
    <Sidebar collapsible="icon" variant="floating">
      <SidebarHeader className="gap-2">
        <div className="relative h-11 transition-[height] duration-200 ease-linear group-data-[collapsible=icon]:h-28">
          <div className="absolute inset-0 flex items-center gap-2 transition-all duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:translate-y-1 group-data-[collapsible=icon]:opacity-0">
            <SidebarMenu className="flex-1">
              <SidebarMenuItem>
                <SidebarMenuButton
                  size="lg"
                  className="h-11 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <ZarityMark
                    variant="reversed"
                    size={32}
                    className="shrink-0"
                  />
                  <div className="flex flex-1 items-center text-left leading-tight">
                    <span className="truncate font-heading text-lg font-bold tracking-[-0.03em] text-sidebar-foreground">
                      zarity
                    </span>
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
            <NotificationBell
              onOpen={() => guardedNavigate("/notifications")}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 rounded-full border border-sidebar-border/60 bg-sidebar-accent/40 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              onClick={toggleSidebar}
              aria-label={
                state === "collapsed" ? "Expand Sidebar" : "Collapse Sidebar"
              }
            >
              <SidebarToggleIcon className="size-4" />
            </Button>
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-0 transition-all duration-200 ease-linear pointer-events-none -translate-y-1 group-data-[collapsible=icon]:pointer-events-auto group-data-[collapsible=icon]:translate-y-0 group-data-[collapsible=icon]:opacity-100">
            <ZarityMark variant="reversed" size={32} className="shrink-0" />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 rounded-full border border-sidebar-border/60 bg-sidebar-accent/40 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              onClick={toggleSidebar}
              aria-label="Expand Sidebar"
            >
              <SidebarToggleIcon className="size-4" />
            </Button>
            <NotificationBell
              onOpen={() => guardedNavigate("/notifications")}
            />
          </div>
        </div>
        <SidebarSeparator />
      </SidebarHeader>
      <SidebarContent className="gap-0">
        {sections.map((section, index) => (
          <div key={section.label}>
            <SidebarGroup className="gap-0.5 px-2 py-0">
              <SidebarGroupLabel className="z-caption h-auto px-2 pb-0.5 pt-0.5 text-sidebar-foreground/55">
                {section.label}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-0.5">
                  {section.label === "Dashboards" && canAccessHome ? (
                    <SidebarMenuItem key="home">
                      <SidebarMenuButton
                        tooltip={consoleHomeNavigation.tooltip}
                        onClick={() =>
                          guardedNavigate(consoleHomeNavigation.href)
                        }
                        isActive={isActive(consoleHomeNavigation.href)}
                        className="z-secondary h-8 rounded-md px-2 text-sidebar-foreground data-[active=true]:bg-sidebar-primary/12 data-[active=true]:font-medium data-[active=true]:text-sidebar-primary hover:bg-sidebar-accent/55"
                      >
                        <Home className={iconography.size.nav} />
                        <span>{consoleHomeNavigation.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ) : null}
                  {section.items.map((item) => {
                    const itemKey = "title" in item ? item.title : item.id;
                    const itemLabel = "title" in item ? item.title : item.label;
                    const itemTooltip =
                      "title" in item ? item.title : item.tooltip;
                    const itemHref = "url" in item ? item.url : item.href;
                    const itemChildren =
                      "children" in item ? item.children : undefined;
                    const hasChildren =
                      !!itemChildren && itemChildren.length > 0;
                    const itemBadge = "badge" in item ? item.badge : null;

                    if (hasChildren && state !== "collapsed") {
                      return (
                        <Collapsible
                          key={itemKey}
                          defaultOpen={item.isActive}
                          className="group/collapsible"
                        >
                          <SidebarMenuItem>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton
                                tooltip={itemTooltip}
                                isActive={item.isActive}
                                className="z-secondary h-8 rounded-md px-2 text-sidebar-foreground data-[active=true]:bg-sidebar-primary/12 data-[active=true]:font-medium data-[active=true]:text-sidebar-primary hover:bg-sidebar-accent/55"
                              >
                                <SiteIcon
                                  name={item.iconKey}
                                  className={iconography.size.nav}
                                />
                                <span>{itemLabel}</span>
                                <ChevronRight className="ml-auto h-4 w-4 text-sidebar-foreground/60 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <SidebarMenuSub>
                                {itemChildren!.map((child) => {
                                  const childActive = isActive(child.href);
                                  return (
                                    <SidebarMenuSubItem key={child.id}>
                                      <SidebarMenuSubButton
                                        isActive={childActive}
                                        onClick={() =>
                                          guardedNavigate(child.href)
                                        }
                                        className="z-secondary cursor-pointer rounded-md text-sidebar-foreground data-[active=true]:bg-sidebar-primary/12 data-[active=true]:font-medium data-[active=true]:text-sidebar-primary hover:bg-sidebar-accent/55"
                                      >
                                        <span>{child.label}</span>
                                      </SidebarMenuSubButton>
                                    </SidebarMenuSubItem>
                                  );
                                })}
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          </SidebarMenuItem>
                        </Collapsible>
                      );
                    }

                    return (
                      <SidebarMenuItem key={itemKey}>
                        <SidebarMenuButton
                          tooltip={itemTooltip}
                          onClick={() => guardedNavigate(itemHref)}
                          isActive={item.isActive}
                          className="z-secondary h-8 rounded-md px-2 text-sidebar-foreground data-[active=true]:bg-sidebar-primary/12 data-[active=true]:font-medium data-[active=true]:text-sidebar-primary hover:bg-sidebar-accent/55"
                        >
                          <SiteIcon
                            name={item.iconKey}
                            className={iconography.size.nav}
                          />
                          <span>{itemLabel}</span>
                          {itemBadge ? (
                            <SidebarMenuBadge>{itemBadge}</SidebarMenuBadge>
                          ) : null}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            {index < sections.length - 1 ? (
              <SidebarSeparator className="my-1.5" />
            ) : null}
          </div>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <div className="px-2 group-data-[collapsible=icon]:hidden">
          <ThemeToggle />
        </div>
        <SidebarSeparator />
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex w-full items-center gap-2.5 rounded-2xl bg-sidebar-accent px-3 py-3 pr-10 transition-[padding,width,height] duration-200 ease-linear group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:rounded-full group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-0">
              <Avatar className="h-10 w-10 shrink-0 border border-sidebar-border/70 bg-card shadow-sm">
                <AvatarImage
                  src={user?.profile_pic || undefined}
                  alt={userName}
                  className="bg-card object-cover"
                />
                <AvatarFallback className="bg-sidebar-primary text-xs font-semibold text-sidebar-primary-foreground">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-1 flex-col justify-center text-left group-data-[collapsible=icon]:hidden">
                <p className="truncate font-heading text-[14px] font-bold leading-[1.15] tracking-[-0.02em] text-sidebar-foreground">
                  {sidebarUserName}
                </p>
                <Badge
                  variant="default"
                  className="mt-1 self-start bg-sidebar-primary/15 px-1.5 py-0 text-[10px] font-medium uppercase tracking-wide text-sidebar-primary hover:bg-sidebar-primary/15"
                >
                  {userRoleLabel}
                </Badge>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuAction
                  aria-label="Open account menu"
                  className="right-2 top-1/2 h-6 w-6 -translate-y-1/2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-background hover:text-sidebar-foreground"
                >
                  <MoreVertical />
                </SidebarMenuAction>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="end" className="w-56">
                <DropdownMenuItem
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="gap-2 text-destructive focus:text-destructive"
                >
                  {isLoggingOut ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogOut className="h-4 w-4" />
                  )}
                  <span>Sign out</span>
                </DropdownMenuItem>
                <div className="px-2 py-1 text-center text-[10px] text-muted-foreground/70">
                  v{parsedVersion}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
        {isStaging && (
          <div className="px-2 group-data-[collapsible=icon]:hidden">
            <div className="flex w-full items-center justify-center rounded-full bg-warning/15 py-1 text-[11px] font-medium text-warning">
              Staging
            </div>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
};
